import type { Lang } from "./types";
import { pathFor, resolveLocation } from "./routes";

// =========================================================================
// BROWSER-LANGUAGE AUTO-SWITCH
// =========================================================================
//
// Sends a visitor whose browser is set to Spanish from "/" to "/es" on their
// first visit. This is deliberately narrow, because automatic language
// redirection is the classic way to make a multilingual site *lose* search
// visibility — if a crawler gets bounced off a page it will not index it.
//
// Every one of these conditions must hold before we switch:
//
//   1. The URL is exactly "/" — the English home page. A visitor who followed a
//      link to a specific English page gets the page they asked for, and the
//      Spanish tree is never redirected away from.
//   2. The request is not a bot or the build-time prerenderer.
//   3. The visitor has no stored language preference. One manual click on the
//      EN/ES switch pins the choice permanently.
//   4. The browser's *primary* language is Spanish.
//
// The switch uses history.replaceState, so it never adds a history entry and
// the Back button still leaves the site instead of ping-ponging / <-> /es.

const PREF_COOKIE = "sln-lang";
const PREF_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Crawlers must reach BOTH language trees, so they are never auto-switched.
 *
 * In practice the language check alone already protects Googlebot — it renders
 * with an en-US locale, so it stays on English anyway. This list is defence in
 * depth for crawlers that report other locales.
 *
 * ReactSnap is the one entry that is load-bearing rather than defensive: the
 * build-time prerenderer must produce ENGLISH html for "/", or every visitor
 * would be served a Spanish home page before React even boots.
 */
const BOT_UA =
  /bot|crawler|crawling|spider|slurp|mediapartners|bingpreview|facebookexternalhit|embedly|pinterest|whatsapp|telegram|lighthouse|reactsnap|headlesschrome|phantomjs|prerender/i;

export function isAutomatedAgent(): boolean {
  if (typeof navigator === "undefined") return true;
  return BOT_UA.test(navigator.userAgent || "");
}

export function readLangPreference(): Lang | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)sln-lang=(en|es)(?:;|$)/);
  return match ? (match[1] as Lang) : null;
}

/**
 * Persist an explicit language choice. Called on every manual EN/ES click, and
 * once after the first auto-detection, so the decision is stable across visits
 * and a manual choice always overrides what the browser reports.
 */
export function writeLangPreference(lang: Lang): void {
  if (typeof document === "undefined") return;
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${PREF_COOKIE}=${lang}; Max-Age=${PREF_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
}

/**
 * True when the visitor's FIRST language preference is Spanish.
 *
 * Only the primary entry counts. Plenty of people carry "es" further down
 * navigator.languages without wanting a Spanish site, so matching anywhere in
 * the list would switch the language on people who never asked for it.
 */
export function browserPrefersSpanish(): boolean {
  if (typeof navigator === "undefined") return false;
  const primary =
    (navigator.languages && navigator.languages[0]) || navigator.language || "";
  return primary.toLowerCase().startsWith("es");
}

/**
 * Resolve the page/language to boot with, applying the auto-switch when all of
 * the conditions documented above are met.
 *
 * Runs during the initial state computation rather than in an effect so the
 * first paint is already in the right language — no flash of English.
 */
export function resolveInitialLocation(pathname: string): {
  page: ReturnType<typeof resolveLocation>["page"];
  lang: Lang;
} {
  const location = resolveLocation(pathname);

  // Guard 1: only a bare visit to the English home page is eligible. Checking
  // the raw pathname (not `location`) matters because resolveLocation() maps
  // unknown paths onto home — a 404-ish URL must not trigger a language switch.
  const isBareRoot = pathname.replace(/\/+$/, "") === "";
  if (!isBareRoot) return location;

  // Guard 2: never redirect crawlers or the prerenderer.
  if (isAutomatedAgent()) return location;

  // Guard 3 + 4: stored choice wins; otherwise consult the browser.
  const stored = readLangPreference();
  const target: Lang = stored ?? (browserPrefersSpanish() ? "es" : "en");
  if (!stored) writeLangPreference(target);

  if (target === "en") return location;

  if (typeof window !== "undefined") {
    window.history.replaceState({}, "", pathFor("home", "es"));
  }
  return { page: "home", lang: "es" };
}
